import { AnimatedModal, EModalVariants, Modal, Overlay } from '@outdoorsyco/bonfire';
import { ReactNode, RefObject, useEffect, useRef } from 'react';
import { FocusScope } from 'react-aria';
import type { OverlayTriggerState } from 'react-stately';

import { useBreakpointContext } from '@/components/common/BreakpointContext';

interface IFieldOverlayProps {
  triggerRef: RefObject<HTMLDivElement>;
  triggerState: OverlayTriggerState;
  content: ReactNode;
  // Optional footer, shown only in the mobile bottom sheet (its own Modal.Footer
  // slot). Desktop popovers don't use it.
  footer?: ReactNode;
}

// Shared responsive presentation for the guided-search field popovers (dates,
// guests). On tablet+ the content is an anchored Overlay next to the trigger; below
// tablet it becomes a bottom-sheet AnimatedModal (matching BillFulfillmentModal).
// This component owns all the modal chrome (close button, content, footer) so the
// fields only supply what goes in each slot.
export const FieldOverlay = ({ triggerRef, triggerState, content, footer }: IFieldOverlayProps) => {
  const { isAboveTablet } = useBreakpointContext();

  // Bonfire's desktop Overlay positions the panel with Popper, which measures it exactly
  // once — when it first attaches to the DOM — and never re-measures on its own. The
  // two-month calendar settles to its final height a beat after it mounts (its height
  // was measured shrinking from ~509px to ~390px), so Popper's `flip` decides against
  // the stale, taller measurement, keeps the panel anchored below the trigger even when
  // it overflows the viewport, and the bottom gets clipped. (Reopening only works
  // because layout is already warm.) A fixed frame/timeout can't reliably catch the
  // settle, so observe the panel and nudge Popper to recompute on every height change —
  // Popper's default eventListeners modifier recomputes on window resize, letting `flip`
  // move the panel above the trigger once it knows the real height.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isAboveTablet || !triggerState.isOpen) return;

    const panel = contentRef.current;
    if (!panel || typeof ResizeObserver === 'undefined') return;

    // Fires once immediately with the current size, then again on each settle. Resizing
    // the window only moves the panel (position, not size), so this can't self-trigger.
    const observer = new ResizeObserver(() => window.dispatchEvent(new Event('resize')));
    observer.observe(panel);

    return () => observer.disconnect();
  }, [isAboveTablet, triggerState.isOpen]);

  if (isAboveTablet) {
    // Overlay only renders while open; it anchors to the trigger.
    if (!triggerState.isOpen) return null;

    return (
      <Overlay
        underlay
        triggerRef={triggerRef}
        triggerState={triggerState}
        popperProps={{ placement: 'bottom-start' }}
        onDismiss={() => triggerState.close()}
        underlayClassname="z-[1299]"
        className="z-[1310]">
        <FocusScope
          // Moving focus into an overlay on open is required for keyboard accessibility
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          restoreFocus
          contain>
          <div ref={contentRef} className="p-4 my-2 bg-white shadow-100 rounded-box">
            {content}
          </div>
        </FocusScope>
      </Overlay>
    );
  }

  // Mobile: a bottom sheet. AnimatedModal only renders its children while open, so
  // the content unmounts on close and remounts on the next open.
  return (
    <AnimatedModal
      isOpen={triggerState.isOpen}
      onClose={() => triggerState.close()}
      variant={EModalVariants.Bottom}
      dialogClassName="overflow-visible"
      withOverlay
      isDismissable>
      <Modal.Actions />
      <Modal.Content>{content}</Modal.Content>
      {footer && <Modal.Footer className="p-0">{footer}</Modal.Footer>}
    </AnimatedModal>
  );
};
